import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/commerce-php/commerce-php/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const InlineAlert = makeShortcode("InlineAlert");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "indexer-optimization"
    }}>{`Indexer optimization`}</h1>
    <p>{`Use batching and table switching to optimize the performance of indexers.`}</p>
    <h2 {...{
      "id": "indexer-batching"
    }}>{`Indexer batching`}</h2>
    <p>{`Adobe Commerce and Magento Open Source can increase the memory for processing a large amount of data by using memory engines instead of InnoDB. The algorithm increases the memory value for the `}<inlineCode parentName="p">{`max_heap_table_size`}</inlineCode>{` and `}<inlineCode parentName="p">{`tmp_table_size`}</inlineCode>{` MySQL parameters.`}</p>
    <p>{`The interface `}<inlineCode parentName="p">{`BatchSizeManagementInterface`}</inlineCode>{` provides the ability to set the MEMORY table size for indexer processes according to batch size and index row size.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`namespace Magento\\Framework\\Indexer;

use Magento\\Framework\\DB\\Adapter\\AdapterInterface;

/**
 * Batch size manager can be used to ensure that MEMORY table has enough memory for data in batch.
 * @api
 */
interface BatchSizeManagementInterface
{
    /**
     * Ensure memory size for data in batch.
     *
     * @param AdapterInterface $adapter database adapter.
     * @param int $batchSize
     * @return void
     */
    public function ensureBatchSize(\\Magento\\Framework\\DB\\Adapter\\AdapterInterface $adapter, $batchSize);
}
`}</code></pre>
    <p>{`The interface `}<inlineCode parentName="p">{`IndexTableRowSizeEstimatorInterface`}</inlineCode>{` calculates the memory size for all rows per entity in the index table. The entity can store several rows in an index table generated by different store dimensions, such as count of websites and customer groups.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`namespace Magento\\Framework\\Indexer;

/**
 * Calculate memory size for entity according different dimensions.
 * @api
 */
interface IndexTableRowSizeEstimatorInterface
{
    /**
     * Calculate memory size for entity row.
     *
     * @return float
     */
    public function estimateRowSize();
}
`}</code></pre>
    <h3 {...{
      "id": "exceeding-allocated-memory-size"
    }}>{`Exceeding allocated memory size`}</h3>
    <p>{`When the allocated memory size for a temporary table will be greater than 20% of `}<inlineCode parentName="p">{`innodb_buffer_pool_size`}</inlineCode>{`, the following message is written to the log.`}</p>
    <p><inlineCode parentName="p">{`Memory size allocated for the temporary table is more than 20% of innodb_buffer_pool_size.`}</inlineCode></p>
    <p>{`To prevent this error message, update `}<inlineCode parentName="p">{`innodb_buffer_pool_size`}</inlineCode>{` or decrease the batch size value (which decreases memory usage for the temporary table).`}</p>
    <h2 {...{
      "id": "batching-configuration"
    }}>{`Batching configuration`}</h2>
    <p>{`Batching is available for the following indexers:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Index name`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Configured object`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Parameter name`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Default value`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`catalog_product_price (Product Price)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Magento\\Catalog\\Model\\`}<br />{`ResourceModel\\Product\\Indexer\\Price\\BatchSizeCalculator`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`batchRowsCount`}{`['default']`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`5000`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`cataloginventory_stock (Stock)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Magento\\CatalogInventory\\Model\\`}<br />{`Indexer\\Stock\\Action\\Full`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`batchRowsCount`}{`['default']`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`200`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`catalog_category_product (Category Products)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Magento\\Catalog\\Model\\`}<br />{`Indexer\\Category\\Product\\Action\\Full`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`batchRowsCount`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`100000`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`catalog_product_attribute (Product Attribute)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Magento\\Catalog\\Model\\`}<br />{`ResourceModel\\Product\\Indexer\\Eav\\BatchSizeCalculator`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`batchSizes`}{`['decimal']`}{`, batchSizes`}{`['source']`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`1000, 1000`}</td>
        </tr>
      </tbody>
    </table>
    <p>{`Changing the batch size can help you optimize indexer running time. For example, for a store with the following characteristics:`}</p>
    <ul>
      <li parentName="ul">{`10 websites`}</li>
      <li parentName="ul">{`10 store groups`}</li>
      <li parentName="ul">{`20 store views`}</li>
      <li parentName="ul">{`300 tier prices`}</li>
      <li parentName="ul">{`About 40,000 products (of which 254 are configurable)`}</li>
    </ul>
    <p>{`Reducing the batch size for `}<inlineCode parentName="p">{`catalog_product_price`}</inlineCode>{` indexer from 5000 to 1000 decreases the execution time from about 4 hours to less than 2 hours. You can experiment to determine the ideal batch size. In general, halving the batch size can decrease the indexer execution time.`}</p>
    <p>{`The following examples illustrate how to define a custom batch size for configurable products. Add these samples to your  `}<inlineCode parentName="p">{`{Your_Module_Name}/etc/di.xml`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-xml"
      }}>{`....
<type name="Magento\\Catalog\\Model\\ResourceModel\\Product\\Indexer\\Price\\BatchSizeCalculator">
    <arguments>
        <argument name="batchRowsCount" xsi:type="array">
            <item name="configurable" xsi:type="number">5000</item>
        </argument>
    </arguments>
</type>
...
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-xml"
      }}>{`<type name="Magento\\CatalogInventory\\Model\\Indexer\\Stock\\Action\\Full">
    <arguments>
        <argument name="batchRowsCount" xsi:type="array">
            <item name="configurable" xsi:type="number">200</item>
        </argument>
    </arguments>
</type>
`}</code></pre>
    <h3 {...{
      "id": "setting-batch-size-with-environment-variables"
    }}>{`Setting batch size with environment variables`}</h3>
    <p>{`As of Adobe Commerce and Magento Open Source 2.4.3, it is possible to configure the batch size with environment variables, or in `}<inlineCode parentName="p">{`app/etc/env.php`}</inlineCode>{`, for the following indexers:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`cataloginventory_stock`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`catalog_category_product`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`catalogsearch_fulltext`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`catalog_product_price`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`catalogpermissions_category`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`inventory`}</inlineCode></li>
    </ul>
    <p>{`Here is an example of the configuration in `}<inlineCode parentName="p">{`app/etc/env.php`}</inlineCode></p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<?php
return [
    'indexer' => [
        'batch_size' => [
            'cataloginventory_stock' => [
                'simple' => 200
            ],
            'catalog_category_product' => 666,
            'catalogsearch_fulltext' => [
                'partial_reindex' => 100,
                'mysql_get' => 500,
                'elastic_save' => 500
            ],
            'catalog_product_price' => [
                'simple' => 200,
                'default' => 500,
                'configurable' => 666
            ],
            'catalogpermissions_category' => 999,
            'inventory' => [
                'simple' => 210,
                'default' => 510,
                'configurable' => 616
            ]
        ]
    ]
];
`}</code></pre>
    <p>{`The batches size for `}<inlineCode parentName="p">{`catalog_category_product`}</inlineCode>{`, `}<inlineCode parentName="p">{`catalogpermissions_category`}</inlineCode>{`, `}<inlineCode parentName="p">{`catalogpermissions_category`}</inlineCode>{` will be set for all product types.`}</p>
    <p>{`Batch size for `}<inlineCode parentName="p">{`catalogsearch_fulltext`}</inlineCode>{` can be set using different parameters.`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`partial_reindex`}</inlineCode>{` - represents how many products will be processed in a partial reindex.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`elastic_save`}</inlineCode>{` - represents how many products will be saved as a batch into Elasticsearch.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`mysql_get`}</inlineCode>{` - represents how many searchable products will be retrieved from Mysql.`}</li>
    </ul>
    <p>{`Batch size for `}<inlineCode parentName="p">{`cataloginventory_stock`}</inlineCode>{`, `}<inlineCode parentName="p">{`catalog_product_price`}</inlineCode>{`, `}<inlineCode parentName="p">{`inventory`}</inlineCode>{` can be set up for each product type. If no batch size is set for a specific product type, the `}<inlineCode parentName="p">{`default`}</inlineCode>{` value is used. We recommend setting the `}<inlineCode parentName="p">{`default`}</inlineCode>{` value for each indexer to allow for different batch sizes per product type.`}</p>
    <h2 {...{
      "id": "indexer-table-switching"
    }}>{`Indexer table switching`}</h2>
    <p>{`Adobe Commerce and Magento Open Source optimize certain indexer processes to prevent deadlocks and wait locks caused by read/write collisions on the same table. In these cases, the application uses separate tables for performing read operations and reindexing. As a result of this table switching process, customers are not impacted when you run a full reindex. For example, when `}<inlineCode parentName="p">{`catalog_product_price`}</inlineCode>{` is reindexing, customers won't be slowed down as they navigate on Categories pages, search products, or user layer navigation filters with price filters.`}</p>
    <p>{`The application uses the following tables to support table switching.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Indexer name`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Tables used`}</th>
          <th parentName="tr" {...{
            "align": null
          }}></th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`catalog_product_price`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`catalog_product_index_price`}</inlineCode>{`, `}<inlineCode parentName="td">{`catalog_product_index_price_replica`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`cataloginventory_stock`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`cataloginventory_stock_status`}</inlineCode>{`, `}<inlineCode parentName="td">{`cataloginventory_stock_status_replica`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`catalog_category_product`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`catalog_category_product_index`}</inlineCode>{`, `}<inlineCode parentName="td">{`catalog_category_product_index_replica`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`catalog_product_attribute`}</inlineCode>{` (select, multiselect attributes)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`catalog_product_index_eav`}</inlineCode>{`, `}<inlineCode parentName="td">{`catalog_product_index_eav_replica`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`catalog_product_attribute`}</inlineCode>{` (decimal values)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`catalog_product_index_eav_decimal`}</inlineCode>{`, `}<inlineCode parentName="td">{`catalog_product_index_eav_decimal_replica`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`catalogrule_rule`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`catalogrule_product`}</inlineCode>{`, `}<inlineCode parentName="td">{`catalogrule_product_replica`}</inlineCode>{`, `}<inlineCode parentName="td">{`catalogrule_product_price`}</inlineCode>{`, `}<inlineCode parentName="td">{`catalogrule_product_price_replica`}</inlineCode>{`, `}<inlineCode parentName="td">{`catalogrule_group_website`}</inlineCode>{`, `}<inlineCode parentName="td">{`catalogrule_group_website_replica`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
      </tbody>
    </table>
    <p>{`Make sure that these indexers are in "Update By Schedule" mode. If "Update On Save" mode is selected, some data can be lost if you make changes during full reindex.`}</p>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`The indexer table switching mechanism requires additional database storage.`}</p>
    <h3 {...{
      "id": "eav-indexer-optimization"
    }}>{`EAV indexer optimization`}</h3>
    <p>{`The Product EAV indexer reorganizes the EAV product structure to a flat structure.
As of Adobe Commerce and Magento Open Source 2.3, under certain circumstances, you can disable this indexer to improve performance. (Its indexation takes about 5 minutes on a large Commerce performance profile.)`}</p>
    <p>{`You cannot disable the Product EAV indexer if you have installed any 3rd-party extensions that rely on the Product EAV indexer.`}</p>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`To determine whether any 3rd-party extensions are using the Product EAV indexer, check the `}<inlineCode parentName="p">{`catalog_product_index_eav`}</inlineCode>{` table for reading/writing activity.`}</p>
    <p>{`To disable the Product EAV indexer in the Admin, go to `}<strong parentName="p">{`Stores`}</strong>{` > Settings > `}<strong parentName="p">{`Configuration`}</strong>{` > `}<strong parentName="p">{`Catalog`}</strong>{` > `}<strong parentName="p">{`Catalog`}</strong>{` > `}<strong parentName="p">{`Catalog Search`}</strong>{` and set the value of `}<strong parentName="p">{`Enable EAV Indexer`}</strong>{` to No.`}</p>
    <h2 {...{
      "id": "customer-group-limitations-by-websites"
    }}>{`Customer group limitations by websites`}</h2>
    <p>{`By default, all websites are assigned to a customer group. If there are a large number of products, websites, customer groups, and B2B shared catalogs, the execution time of the `}<inlineCode parentName="p">{`Product Price`}</inlineCode>{` and `}<inlineCode parentName="p">{`Catalog Rule`}</inlineCode>{` indexers may be quite long. This is because each website, customer group, shared catalog, and product SKU must all be indexed against each other in the product price and catalog rule indexer-related tables.`}</p>
    <p>{`You can exclude websites from a customer group or shared catalog on the `}<a parentName="p" {...{
        "href": "https://experienceleague.adobe.com/en/docs/commerce-admin/customers/customer-groups#create-a-customer-group"
      }}>{`Customer Groups page`}</a>{`. Excluding websites can decrease `}<inlineCode parentName="p">{`Product Price`}</inlineCode>{` and `}<inlineCode parentName="p">{`Catalog Rule`}</inlineCode>{` indexing time, because excluded websites are not indexed.`}</p>
    <p>{`When a customer group with a new, excluded website is saved, the `}<inlineCode parentName="p">{`Product Price`}</inlineCode>{`, `}<inlineCode parentName="p">{`Catalog Rule`}</inlineCode>{` and `}<inlineCode parentName="p">{`Catalog Search`}</inlineCode>{` indexes are invalidated. If you have a large number of products, websites, and customer groups, we recommended you pause the reindex process until you have excluded websites from the customer groups.`}</p>
    <p>{`You cannot exclude websites from a shared catalog on a shared catalog create/edit page. However, you can exclude websites from a customer group that is created from a shared catalog.`}</p>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`By default, no websites are excluded from a customer group or shared catalog.
You cannot exclude websites from the `}<inlineCode parentName="p">{`NOT LOGGED IN`}</inlineCode>{` customer group.`}</p>
    <InlineAlert variant="warning" slots="text" mdxType="InlineAlert" />
    <p>{`When a website is added to the excluded customer group and a customer who belongs to this group is trying to login to their account on the excluded website, the customer gets the `}<inlineCode parentName="p">{`"This website is excluded from customer's group."`}</inlineCode>{` warning.
Customers are not allowed to login to the website even if `}<a parentName="p" {...{
        "href": "https://experienceleague.adobe.com/en/docs/commerce-admin/customers/customer-accounts/customer-account-scope"
      }}>{`the scope of customer accounts is shared with all websites`}</a>{`, because there are no prices in the index table for this user.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      